/*
 * Web-HMI fuer Endkunden (18.10.2026): die HMI der zugewiesenen Anlagen im
 * Browser oder am Handy, ohne HVACloud-Rahmen und ohne Dashboard. Dieselbe
 * Darstellung wie die Laufzeit am Panel, nur mit HVACloud als Datenquelle
 * (Hub-Abo, Werte, Verlauf, Schreiben). Der Server gibt diese Pfade nur fuer
 * zugewiesene Anlagen frei (WebHmiAccessMiddleware) und beendet die Sitzung
 * nach einer harten Grenze; dauerhaftes Mitlesen ist Sache der lokalen HMI.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api, ApiError } from '../api/client';
import { sessionSignal, signOut, webHmiExpiredSignal } from '../auth/session';
import type { HmiScreen, WidgetDefinition } from '../hmi/widgets';
import type { HmiLeafMeta } from '../hmi/leafMeta';
import { LivePlantBindingProvider } from '../hmi/PointBindingProvider';
import { WithSimulation } from '../hmi/WithSimulation';
import { LeafMetaLookupProvider } from '../hmi/LeafMetaContext';
import { FreeStage } from '../hmi/panel/FreeStage';
import { ThemeScope, useEffectiveScheme, useStoredScheme } from '../hmi/panel/ThemeScope';
import { HmiRuntimeProvider, type HmiRuntime } from '../hmi/panel/runtimeContext';
import { pickTarget } from '../runtime/targetPicker';
import { GridScreen, useHashScreen, useViewport } from '../runtime/RuntimeApp';
import { stripLocalOnly } from '../hmi/localOnly';
import '../hmi/hmiCell.css';
import '../runtime/runtime.css';
import { BRAND } from '../brand/brand';
import './WebHmiPage.css';

interface MyPlant { plantId: string; name: string; access: string }

interface DeviceProject {
  name: string;
  body: { appearance?: unknown; widgets: WidgetDefinition[]; screens: HmiScreen[] };
  points: Record<string, HmiLeafMeta>;
}

export function WebHmiPage({ plantId }: { plantId?: string }) {
  const { route } = useLocation();
  const [plants, setPlants] = useState<MyPlant[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<MyPlant[]>('/api/webhmi/plants')
      .then(setPlants)
      .catch((e) => setError(e instanceof ApiError ? `Anlagen nicht ladbar (HTTP ${e.status}).` : String(e)));
  }, []);

  useInstallableApp();

  const chosen = plantId ?? (plants?.length === 1 ? plants[0]!.plantId : null);
  const current = plants?.find((p) => p.plantId === chosen) ?? null;

  return (
    <div class="hv-webhmi">
      <header class="hv-webhmi__bar">
        <span class="hv-webhmi__name">{current?.name ?? 'Meine Anlagen'}</span>
        {plants && plants.length > 1 && current && (
          <button type="button" class="hv-webhmi__link" onClick={() => route('/m')}>Anlage wechseln</button>
        )}
        <SessionClock />
        <MyData />
        <button type="button" class="hv-webhmi__link" onClick={signOut}>Abmelden</button>
      </header>
      <main class="hv-webhmi__main">
        {error && <p class="hv-webhmi__msg">{error}</p>}
        {!error && !plants && <p class="hv-webhmi__msg">Lade…</p>}
        {plants && plants.length === 0 && (
          <p class="hv-webhmi__msg">Dir ist noch keine Anlage zugewiesen. Bitte wende dich an deinen Installateur.</p>
        )}
        {plants && plants.length > 1 && !chosen && (
          <ul class="hv-webhmi__list">
            {plants.map((p) => (
              <li key={p.plantId}>
                <button type="button" onClick={() => route(`/m/${p.plantId}`)}>
                  <b>{p.name}</b>
                  <span>{p.access === 'ansehen' ? 'nur ansehen' : 'bedienen'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {chosen && <PlantHmi key={chosen} plantId={chosen} />}
      </main>
      <ExpiredOverlay />
    </div>
  );
}

/**
 * Web-HMI als installierbare App (PWA): Manifest der Marke mit Start und
 * Bereich /m, eigenes Icon, ohne Browserleiste. Nur auf dieser Seite, die
 * HVACloud selbst bleibt eine normale Webseite.
 */
function useInstallableApp() {
  useEffect(() => {
    const head = document.head;
    const added: HTMLElement[] = [];
    const add = (tag: 'link' | 'meta', attrs: Record<string, string>) => {
      const el = document.createElement(tag);
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      head.appendChild(el);
      added.push(el);
    };
    // Das Manifest des Dashboards fuer diese Seite herausnehmen, sonst
    // installierte "Zum Startbildschirm" das Dashboard.
    const dashboard = head.querySelector<HTMLLinkElement>('link[rel="manifest"][data-app="dashboard"]');
    dashboard?.remove();
    add('link', { rel: 'manifest', href: `/brand/${BRAND.id}/webhmi.manifest.json` });
    add('link', { rel: 'apple-touch-icon', href: `/brand/${BRAND.id}/icon-192.png`, sizes: '192x192' });
    add('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'apple-mobile-web-app-title', content: BRAND.id === 'austriaemail' ? 'Austria Email' : BRAND.productName });
    add('meta', { name: 'apple-mobile-web-app-status-bar-style', content: 'black' });
    return () => {
      for (const el of added) el.remove();
      if (dashboard) head.appendChild(dashboard);
    };
  }, []);
}

function PlantHmi({ plantId }: { plantId: string }) {
  const [project, setProject] = useState<DeviceProject | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.get<DeviceProject>(`/api/plants/${plantId}/hmi/device-project`)
      .then(setProject)
      .catch((e) => setError(e instanceof ApiError && e.status === 404
        ? 'Für diese Anlage ist noch keine Anzeige freigegeben.'
        : 'Anzeige nicht ladbar.'));
  }, [plantId]);
  if (error) return <p class="hv-webhmi__msg">{error}</p>;
  if (!project) return <p class="hv-webhmi__msg">Lade Anzeige…</p>;
  return <ProjectView plantId={plantId} project={project} />;
}

function ProjectView({ plantId, project }: { plantId: string; project: DeviceProject }) {
  // Alles wie am Bedienfeld, ausser dem, was nur dort hingehoert (QR-Code
  // "Benutzer hinzufuegen"): das kommt hier gar nicht erst an.
  const body = useMemo(() => stripLocalOnly(project.body), [project]);
  const widgetsById = useMemo(() => new Map(body.widgets.map((w) => [w.id, w])), [body]);
  const meta = useMemo(() => {
    const m = new Map<string, HmiLeafMeta>();
    for (const [k, v] of Object.entries(project.points ?? {})) m.set(k.toLowerCase(), v);
    return m;
  }, [project]);
  const lookup = useMemo(() => (key: string | null | undefined) => (key ? meta.get(key.toLowerCase()) : undefined), [meta]);
  const [screenId, navigate] = useHashScreen(body.screens);
  const viewport = useViewport();
  const screen = body.screens.find((s) => s.id === screenId) ?? null;
  const target = screen ? pickTarget(screen.layouts.map((l) => l.target), viewport, null) : null;
  const layout = screen?.layouts.find((l) => l.target === target) ?? null;
  const [stored, setScheme] = useStoredScheme('webhmi-scheme');
  const scheme = useEffectiveScheme(body.appearance, stored);

  const runtime: HmiRuntime = {
    screens: body.screens.map((s) => ({ id: s.id, label: s.label, iconRef: s.iconRef })),
    currentScreenId: screenId,
    navigate,
    assetUrl: (id) => `/api/hmi-assets/${encodeURIComponent(id)}/content`,
    editing: false,
    scheme,
    setScheme,
  };

  return (
    <ThemeScope appearance={body.appearance} scheme={stored} class="hv-rt hv-webhmi__stage">
      <HmiRuntimeProvider value={runtime}>
        <LivePlantBindingProvider plantId={plantId}>
          <WithSimulation>
          <LeafMetaLookupProvider lookup={lookup}>
            {!screen && <p class="hv-webhmi__msg">Die Anzeige hat keine Seiten.</p>}
            {screen && layout && (layout.mode === 'free'
              ? <FreeStage key={`${screen.id}-${layout.target}`} layout={layout} widgetIds={screen.widgetIds}
                           widgetsById={widgetsById} fit="contain" />
              : <GridScreen layout={layout} widgetIds={screen.widgetIds} widgetsById={widgetsById} />)}
          </LeafMetaLookupProvider>
          </WithSimulation>
        </LivePlantBindingProvider>
      </HmiRuntimeProvider>
    </ThemeScope>
  );
}

/**
 * Auskunft und Löschen (DSGVO Art. 15 und 17, docs/registrierportal.md §8).
 * Löschen nur für Endkunden; der Server prüft das selbst.
 */
function MyData() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = sessionSignal.value.user;
  const webHmiOnly = user?.webHmiOnly === true;

  const remove = async () => {
    if (!window.confirm('Ihr Konto wird gelöscht. Sie verlieren den Zugriff auf alle Anlagen. Fortfahren?')) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete('/api/webhmi/me');
      window.location.href = '/';
    } catch (e) {
      setError(e instanceof ApiError ? ((e.body as { error?: string } | null)?.error ?? `HTTP ${e.status}`) : String(e));
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" class="hv-webhmi__link" onClick={() => setOpen((o) => !o)} aria-expanded={open}>Meine Daten</button>
      {open && (
        <div class="hv-webhmi__data" role="dialog" aria-label="Meine Daten">
          <a class="hv-webhmi__link" href="/api/webhmi/me/export" download="meine-daten.json">Alle Daten herunterladen</a>
          {webHmiOnly && (
            <button type="button" class="hv-webhmi__link hv-webhmi__link--danger" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Lösche…' : 'Konto löschen'}
            </button>
          )}
          {error && <span class="hv-webhmi__msg">{error}</span>}
        </div>
      )}
    </>
  );
}

/** Restzeit der Web-HMI; die Grenze selbst setzt der Server durch. Endkunden
 *  zaehlen ab der Anmeldung, Techniker mit eigener Anlage ab dem ersten
 *  Oeffnen der Web-HMI (GET /api/webhmi/session). */
function SessionClock() {
  const [ends, setEnds] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const load = () => api.get<{ endsAt: string | null }>('/api/webhmi/session')
      .then((r) => setEnds(r.endsAt)).catch(() => { /* Grenze greift trotzdem am Server */ });
    void load();
    const poll = window.setInterval(load, 30_000);
    const tick = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => { window.clearInterval(poll); window.clearInterval(tick); };
  }, []);
  useEffect(() => {
    if (!ends) return undefined;
    const id = window.setTimeout(() => { webHmiExpiredSignal.value = true; }, Math.max(0, Date.parse(ends) - Date.now()));
    return () => window.clearTimeout(id);
  }, [ends]);
  if (!ends) return null;
  const seconds = Math.max(0, Math.round((Date.parse(ends) - now) / 1000));
  const text = seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${seconds} s`;
  return <span class={`hv-webhmi__clock${seconds <= 60 ? ' is-low' : ''}`} title="Danach ist eine neue Anmeldung nötig.">
    Noch {text}
  </span>;
}

function ExpiredOverlay() {
  if (!webHmiExpiredSignal.value) return null;
  const onlyWebHmi = sessionSignal.value.user?.webHmiOnly ?? true;
  return (
    <div class="hv-rt-note" role="dialog" aria-modal="true">
      <div class="hv-rt-note__card">
        <span class="hv-rt-note__kicker">Zeit abgelaufen</span>
        <h2 class="hv-rt-note__title">Fernbedienung beendet</h2>
        <div class="hv-rt-note__body">
          <p>Die Fernbedienung endet aus Sicherheitsgründen nach wenigen Minuten.
            Für eine dauerhafte Anzeige ist das Bedienfeld an der Anlage da.</p>
          {!onlyWebHmi && <p>HVACloud selbst bleibt angemeldet; die Fernbedienung geht nach einer neuen Anmeldung wieder.</p>}
        </div>
        {onlyWebHmi
          ? <button type="button" class="hv-rt-note__ok" onClick={signOut}>Neu anmelden</button>
          : <a class="hv-rt-note__ok hv-webhmi__back" href="/">Zurück zu HVACloud</a>}
      </div>
    </div>
  );
}
